import axios from 'axios'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import Plot from 'react-plotly.js'
import { Loader, Message } from 'semantic-ui-react'
import { setBurstLive, setBurstNoData } from '../../../../../store/fluxLiveSlice'
import { useAppDispatch } from '../../../../../store/hooks'
import { serverUrl } from '../../../../../utilites/config'
import { reconnectingSocket } from '../../../../../utilites/socket'

const UTC_TICK_FORMAT = '%H:%M:%S'  // Same format as FLUX chart
const formatToUtcIso = (value: any) => {
  const date = value instanceof Date ? value : new Date(value)
  return Number.isNaN(date.getTime()) ? value : date.toISOString()
}
const formatUtcForFilename = () => {
  const d = new Date()
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${pad(d.getUTCHours())}${pad(d.getUTCMinutes())}${pad(d.getUTCSeconds())}_${pad(d.getUTCDate())}-${pad(
    d.getUTCMonth() + 1,
  )}-${d.getUTCFullYear()}`
}

const MAX_POINTS_CACHE = 600 // cap cached points to avoid unbounded localStorage
const WINDOW_MS_LIMIT = 5 * 60_000 // keep only last 5 minutes on the chart (4 min cold cache + 1 min hot cache)
const SIGMA_CLIPPING_THRESHOLD = 2.8 // sigma threshold for color scale clamping
const WEBSOCKET_URL = `${serverUrl}/websocket` // shared STOMP endpoint for every band subscription
const FREQUENCY_TICK_VALS = [0, 256, 512, 768, 1023] // 5 evenly spaced channel indices; both bands have 1024 channels

// Calculate mean and standard deviation for 2D z array (for sigma clamping)
const calculateZStats = (z: number[][]): { mean: number; std: number } | null => {
  const values: number[] = []
  for (const row of z) {
    for (const val of row) {
      if (Number.isFinite(val)) {
        values.push(val)
      }
    }
  }
  if (values.length === 0) return null

  const mean = values.reduce((a, b) => a + b, 0) / values.length
  const variance = values.reduce((acc, val) => acc + (val - mean) ** 2, 0) / values.length
  const std = Math.sqrt(variance)

  return { mean, std }
}

const HEATMAP_COLORSCALE: [string, string][] = [
  ['0.0', 'rgb(148,0,211)'],    // Violet
  ['0.17', 'rgb(75,0,130)'],    // Indigo
  ['0.33', 'rgb(0,0,255)'],     // Blue
  ['0.5', 'rgb(0,255,0)'],      // Green
  ['0.67', 'rgb(255,255,0)'],   // Yellow
  ['0.83', 'rgb(255,127,0)'],   // Orange
  ['1.0', 'rgb(255,0,0)'],      // Red
]

// In-memory cache for both bands - persists across renders and tab switches
const bandDataCache: { [band: string]: SpectrogramData } = {
  '107': { x: [], z: [], type: 'heatmap', colorscale: HEATMAP_COLORSCALE },
  '30': { x: [], z: [], type: 'heatmap', colorscale: HEATMAP_COLORSCALE },
}
// Track if initial load has been done for each band
const bandLoadedState: { [band: string]: boolean } = { '107': false, '30': false }

interface SpectrogramData {
  x: Date[]
  z: number[][]
  type: string
  colorscale: [string, string][]
}

interface SpectrogramProps {
  type: string
  // eslint-disable-next-line no-unused-vars
  onHasDataChange?: (hasData: boolean) => void
}

function Spectrogram({ type, onHasDataChange }: SpectrogramProps) {
  const connection107Ref = useRef<any>(null)
  const connection30Ref = useRef<any>(null)
  const [data, setData] = useState<Array<SpectrogramData>>([{ x: [], z: [], type: 'heatmap', colorscale: [['0', '']] }])
  const [isSpectrogramLoading, setSpectrogramLoading] = useState(false)
  const [, setHasActiveBurst] = useState<boolean>(true)
  const localStorageTimerRef = useRef<NodeJS.Timeout | null>(null)
  const dispatch = useAppDispatch()
  const downloadFilename = useMemo(() => {
    const bandLabel = type === '30' ? 'F30' : 'F10.7'
    return `[BURST][${bandLabel}][1s]_${formatUtcForFilename()}`
  }, [type])
  const currentDateLabel = useMemo(() => {
    const d = new Date()
    const pad = (n: number) => String(n).padStart(2, '0')
    return `${pad(d.getUTCDate())}-${pad(d.getUTCMonth() + 1)}-${d.getUTCFullYear()}`
  }, [])
  const hasData = useMemo(
    () => data.some(trace => Array.isArray(trace.x) && trace.x.length > 0),
    [data],
  )

  // Compute sigma clamped bounds for color scale (throttled to reduce CPU usage)
  const zBoundsRef = useRef<{ zmin: number; zmax: number } | null>(null)
  const zBoundsLastCalcRef = useRef<number>(0)
  const ZBOUNDS_THROTTLE_MS = 10_000 // Recalculate bounds every 10 seconds

  const zBounds = useMemo(() => {
    if (!data.length || !data[0]?.z?.length) return null

    const now = Date.now()
    // Use cached bounds if within throttle window and we have cached values
    if (zBoundsRef.current && now - zBoundsLastCalcRef.current < ZBOUNDS_THROTTLE_MS) {
      return zBoundsRef.current
    }

    const stats = calculateZStats(data[0].z)
    if (!stats) return zBoundsRef.current // Return cached if calculation fails

    const newBounds = {
      zmin: Math.max(0, stats.mean - SIGMA_CLIPPING_THRESHOLD * stats.std), // Don't go below 0 for flux values
      zmax: stats.mean + SIGMA_CLIPPING_THRESHOLD * stats.std,
    }

    zBoundsRef.current = newBounds
    zBoundsLastCalcRef.current = now

    return newBounds
  }, [data])

  // Notify parent when hasData changes
  useEffect(() => {
    onHasDataChange?.(hasData)
  }, [hasData, onHasDataChange])

  const band = type === '30' ? '30' : '107'
  const bandRef = useRef(band)

  // Keep bandRef in sync with current band
  useEffect(() => {
    bandRef.current = band
    // Reset zBounds cache when band changes to force immediate recalculation
    zBoundsRef.current = null
    zBoundsLastCalcRef.current = 0
  }, [band])
  const cacheKey = useMemo(() => `burst-spectrogram-cache-${type}`, [type])
  
  const pruneToWindow = useCallback((trace: SpectrogramData): SpectrogramData => {
    if (!trace.x || !trace.x.length) return trace
    const cutoff = Date.now() - WINDOW_MS_LIMIT
    const startIdx = trace.x.findIndex(d => d.getTime() >= cutoff)
    if (startIdx === -1) {
      // everything is older than window; drop all
      return { ...trace, x: [], z: trace.z.map(() => []) }
    }
    if (startIdx === 0) return trace
    return {
      ...trace,
      x: trace.x.slice(startIdx),
      z: trace.z.map(row => row.slice(startIdx)),
    }
  }, [])

  // Helper to update in-memory cache for a specific band
  const updateBandCache = useCallback((targetBand: string, newData: SpectrogramData) => {
    bandDataCache[targetBand] = pruneToWindow(newData)
  }, [pruneToWindow])

  // Merge new data into existing cache for a band
  const mergeBandData = useCallback((targetBand: string, dataFromWS: any[], mode: 'append' | 'prepend' = 'append') => {
    if (!dataFromWS || !dataFromWS.length) return

    const newData: SpectrogramData = { x: [], z: [], type: 'heatmap', colorscale: HEATMAP_COLORSCALE }
    dataFromWS.forEach(item => {
      newData.x.push(new Date(item.date))
    })
    const elementsInArray = dataFromWS[0].value.length
    for (let index = 0; index < elementsInArray; index++) {
      const elements: number[] = []
      dataFromWS.forEach(item2 => {
        const v = item2.value[index]
        elements.push(v === null || v === undefined ? Number.NaN : Number(v))
      })
      newData.z.push(elements)
    }

    const existing = bandDataCache[targetBand]
    if (existing.z.length && existing.z.length === newData.z.length) {
      // Merge with existing data
      const merged: SpectrogramData = {
        x: mode === 'append' ? existing.x.concat(newData.x) : newData.x.concat(existing.x),
        z: existing.z.map((row, i) => mode === 'append' ? row.concat(newData.z[i]) : newData.z[i].concat(row)),
        type: 'heatmap',
        colorscale: HEATMAP_COLORSCALE,
      }
      updateBandCache(targetBand, merged)
    } else if (newData.z.length > 0) {
      // No existing data or dimension mismatch - use new data
      updateBandCache(targetBand, newData)
    }
  }, [updateBandCache])

  // Switch to cached data when changing tabs
  useEffect(() => {
    const cachedData = bandDataCache[band]
    if (cachedData.x.length > 0) {
      // Instant switch - data already in memory
      setData([pruneToWindow(cachedData)])
      setSpectrogramLoading(false)
    } else {
      // No cached data yet - will be loaded by the fetch effect
      setData([{ x: [], z: [], type: 'heatmap', colorscale: HEATMAP_COLORSCALE }])
    }
  }, [band, pruneToWindow])

  // Persist data to localStorage with debouncing
  useEffect(() => {
    if (!data.length || !data[0]?.x?.length) return undefined

    if (localStorageTimerRef.current) {
      clearTimeout(localStorageTimerRef.current)
    }
    
    localStorageTimerRef.current = setTimeout(() => {
      try {
        const x = data[0].x.map(d => d.getTime()).slice(-MAX_POINTS_CACHE)
        const z = data[0].z.map(arr => arr.slice(-MAX_POINTS_CACHE))
        localStorage.setItem(cacheKey, JSON.stringify({ x, z }))
      } catch {
        // ignore cache errors
      }
    }, 5000)
    
    return () => {
      if (localStorageTimerRef.current) {
        clearTimeout(localStorageTimerRef.current)
      }
    }
  }, [data, cacheKey])

  // Load data using hot/cold cache endpoints for BOTH bands
  useEffect(() => {
    let isCancelled = false

    const loadBandData = async (targetBand: string, isCurrentBand: boolean) => {
      // If cache is empty, reset the loaded state to allow refetching
      // This handles the case where user returns to this tab after being away
      if (bandLoadedState[targetBand] && bandDataCache[targetBand].x.length === 0) {
        bandLoadedState[targetBand] = false
      }

      // Skip if already loaded for this band AND cache has data
      if (bandLoadedState[targetBand] && bandDataCache[targetBand].x.length > 0) {
        if (isCurrentBand) {
          setData([pruneToWindow(bandDataCache[targetBand])])
          setSpectrogramLoading(false)
          setHasActiveBurst(true)
        }
        return
      }

      try {
        if (isCurrentBand) {
          setSpectrogramLoading(true)
        }

        // Step 1: Fetch hot cache (current minute) - fast, small response
        const hotResp = await axios.get(`${serverUrl}/burst/hotCache`, { params: { band: targetBand } })

        if (!isCancelled && hotResp.data && hotResp.data.length > 0) {
          mergeBandData(targetBand, hotResp.data, 'append')
          if (isCurrentBand) {
            setData([pruneToWindow(bandDataCache[targetBand])])
            setSpectrogramLoading(false) // Show data immediately
            setHasActiveBurst(true)
          }
        }

        // Step 2: Fetch cold cache (previous 4 minutes) - larger but stable
        const coldResp = await axios.get(`${serverUrl}/burst/coldCache`, { params: { band: targetBand } })

        if (!isCancelled && coldResp.data && coldResp.data.length > 0) {
          mergeBandData(targetBand, coldResp.data, 'prepend')
          if (isCurrentBand) {
            setData([pruneToWindow(bandDataCache[targetBand])])
            setHasActiveBurst(true)
          }
        }

        bandLoadedState[targetBand] = true

        if (isCurrentBand && !isCancelled) {
          setSpectrogramLoading(false)
          // If no data was loaded, assume no active burst
          if (bandDataCache[targetBand].x.length === 0) {
            setHasActiveBurst(false)
          }
        }
      } catch (error) {
        console.warn(`Failed to load cache for band ${targetBand}:`, error)
        if (isCurrentBand && !isCancelled) {
          setSpectrogramLoading(false)
          setHasActiveBurst(false)
        }
      }
    }

    // Load both bands - current band first for immediate display
    const loadAllBands = async () => {
      // Load current band first (with loading indicator)
      await loadBandData(band, true)
      
      // Then load other band in background (no loading indicator)
      const otherBand = band === '107' ? '30' : '107'
      if (!isCancelled) {
        loadBandData(otherBand, false)
      }
    }

    loadAllBands()

    return () => {
      isCancelled = true
    }
  }, [band, mergeBandData, pruneToWindow])

  // Refs to hold the latest handler functions - avoids stale closures
  // eslint-disable-next-line no-unused-vars
  const handler107Ref = useRef<((d: any) => void) | null>(null)
  // eslint-disable-next-line no-unused-vars
  const handler30Ref = useRef<((d: any) => void) | null>(null)

  // Update handler refs when dependencies change
  useEffect(() => {
    handler107Ref.current = (d: any) => {
      try {
        if (d.body !== 'NO_DATA') {
          setHasActiveBurst(true)
          dispatch(setBurstLive())
          const dataFromWS = JSON.parse(d.body)

          if (!dataFromWS || !dataFromWS.length || !dataFromWS[0]?.value) {
            console.warn('[Spectrogram 107] Invalid data structure:', dataFromWS)
            return
          }

          mergeBandData('107', dataFromWS, 'append')

          if (bandRef.current === '107') {
            setData([pruneToWindow(bandDataCache['107'])])
          }
        } else {
          setHasActiveBurst(false)
          dispatch(setBurstNoData())
        }
      } catch (error) {
        console.error('[Spectrogram 107] Error processing WebSocket data:', error)
      }
    }

    handler30Ref.current = (d: any) => {
      try {
        if (d.body !== 'NO_DATA') {
          setHasActiveBurst(true)
          dispatch(setBurstLive())
          const dataFromWS = JSON.parse(d.body)

          if (!dataFromWS || !dataFromWS.length || !dataFromWS[0]?.value) {
            console.warn('[Spectrogram 30] Invalid data structure:', dataFromWS)
            return
          }

          mergeBandData('30', dataFromWS, 'append')

          if (bandRef.current === '30') {
            setData([pruneToWindow(bandDataCache['30'])])
          }
        } else {
          setHasActiveBurst(false)
          dispatch(setBurstNoData())
        }
      } catch (error) {
        console.error('[Spectrogram 30] Error processing WebSocket data:', error)
      }
    }

  }, [dispatch, mergeBandData, pruneToWindow])

  // Subscribe to BOTH WebSocket topics - only run once on mount
  useEffect(() => {
    // Wrapper functions that delegate to the latest handler refs
    const handle107 = (d: any) => handler107Ref.current?.(d)
    const handle30 = (d: any) => handler30Ref.current?.(d)

    // Subscribe to F10.7 (107) band
    connection107Ref.current = reconnectingSocket(
      WEBSOCKET_URL,
      handle107,
      '/topic/burst-live',
      window.location.href,
    )

    // Subscribe to F30 band
    connection30Ref.current = reconnectingSocket(
      WEBSOCKET_URL,
      handle30,
      '/topic/burst-live-30',
      window.location.href,
    )

    // Burst status is RadioSpectrogram's; this component only plots burst-live data.

    return () => {
      // Cleanup 107 connection
      const conn107 = connection107Ref.current
      if (conn107) {
        conn107.disconnect?.()
      }

      // Cleanup 30 connection
      const conn30 = connection30Ref.current
      if (conn30) {
        conn30.disconnect?.()
      }
    }
  }, []) // Empty deps - only subscribe once on mount

  // If no data, show info message
  if (!hasData && !isSpectrogramLoading) {
    return (
      <div style={{ height: 400, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <Message info>
          <Message.Header>No Data Available</Message.Header>
          <p>There is currently no spectrogram data available.</p>
        </Message>
      </div>
    )
  }

  return data.length ? (
    <div>
      <Loader
        active={isSpectrogramLoading}
        style={{ position: 'absolute', left: 0, right: 0, marginLeft: 'auto', marginRight: 'auto' }}
      />
      <Plot
        useResizeHandler={true}
        style={{ width: '100%' }}
        config={{ toImageButtonOptions: { filename: downloadFilename } }}
        data={
          hasData
            ? data.map(trace => ({
              ...trace,
              x: Array.isArray(trace.x) ? trace.x.map(formatToUtcIso) : trace.x,
              zmin: zBounds?.zmin,
              zmax: zBounds?.zmax,
              colorbar: {
                title: {
                  text: 'SFU',
                  side: 'right',
                },
              },
            }))
            : [{ x: [], z: [], type: 'heatmap', colorscale: [['0', '']] }]
        }
        layout={{
          autosize: true,
          showlegend: false,
          xaxis: hasData
            ? {
              type: 'date',
              tickformat: UTC_TICK_FORMAT,
              hoverformat: UTC_TICK_FORMAT,
              fixedrange: true,
              title: {
                text: `UTC — ${currentDateLabel}`,
              },
            }
            : { visible: false },
          yaxis: hasData ? {
            fixedrange: true,
            autorange: 'reversed',
            title: {
              text: 'Frequency (MHz)',
            },
            // Configure ticks based on band
            tickmode: 'array',
            tickvals: FREQUENCY_TICK_VALS,
            ticktext: type === '30'
              ? ['970', '995', '1020', '1045', '1070']  // F30: 970-1070 MHz
              : ['2750', '2775', '2800', '2825', '2850'], // F10.7: 2750-2850 MHz
          } : {
            fixedrange: true,
            autorange: 'reversed',
          },
          zaxis: {
            fixedrange: true,
            title: {
              text: 'Frequency (MHz)',
            },
          },
          margin: {
            l: 80,
            r: 50,
            b: 50,
            t: 50,
            pad: 4,
          },
        }}
      />
    </div>
  ) : (
    <div style={{ height: 400, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
      <Loader />
    </div>
  )
}

export default Spectrogram
